import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Calendar, Clock, MapPin, TrendingUp, Edit3, Phone, Star } from 'lucide-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import logo from '../images/images.png';

const LightModeLoanCard = ({
  id,
  name,
  loanSize,
  projectedSalePrice,
  businessState,
  businessAge,
  submittedAt,
  ebitda,
  lastUserUpdate,
  onClick,
  isPrequalification,
  action,
  called = false,
  allDocumentsUploaded = false,
  personalGuarantors = [],
  isFollowUp = false,
  onCallStatusChange,
  loanPurpose = 'Acquisition',
  notes = '',
  isCallsTab = false,
  starred = false,
  followUpDate = '',
  termSheetRequested = false,
  prequalified = false,
  hasChosenListing = false,
  chosenListingName = '',
  isPursuingOtherBrokersDeal = false,
  otherBrokerDealName = '',
}) => {
  const navigate = useNavigate();
  const [isCalled, setIsCalled] = useState(called);
  const [currentAction, setCurrentAction] = useState(action);
  const isWorkingCapital = loanPurpose === 'workingCapital';
  const isNonSBA = loanPurpose === 'workingCapital-nonsba';
  const [currentNotes, setCurrentNotes] = useState(notes);
  const [isStarred, setIsStarred] = useState(starred);
  const displayedAmount = Number(isPrequalification ? projectedSalePrice : loanSize);
  const displayedEbitda = Number(ebitda);

  const handleCardClick = () => {
    navigate(`/loan-details/${id}`);
  };

  const handleCallToggle = async (e) => {
    e.stopPropagation();
    try {
      const firstName = personalGuarantors[0]?.firstName || 'Customer';
      const loanDocRef = doc(db, 'loanApplications', id);
      
      if (isFollowUp) {
        // Handle follow-up call toggle
        const newFollowupCalledStatus = !isCalled;
        
        if (!isCalled) { // If currently "No" and changing to "Yes"
          // Get current displayDate and add 3 days to it
          const currentDisplayDate = await getDoc(loanDocRef);
          const currentDate = currentDisplayDate.data().displayDate.toDate();
          const nextFollowUpDate = new Date(currentDate);
          nextFollowUpDate.setDate(nextFollowUpDate.getDate() + 3);
          
          console.log('Setting next follow-up:', {
            followupCalled: false,
            displayDate: nextFollowUpDate,
            followUpDate: nextFollowUpDate,
            action: `Call ${firstName} to check on documents by ${nextFollowUpDate.toLocaleDateString()}`
          });

          await updateDoc(loanDocRef, {
            followupCalled: false,
            displayDate: nextFollowUpDate,
            followUpDate: nextFollowUpDate,
            action: `Call ${firstName} to check on documents by ${nextFollowUpDate.toLocaleDateString()}`
          });
          
          setIsCalled(false);
          setCurrentAction(`Call ${firstName} to check on documents by ${nextFollowUpDate.toLocaleDateString()}`);
          
          if (onCallStatusChange) {
            onCallStatusChange();
          }
        } else {
          // If marking as not called
          await updateDoc(loanDocRef, {
            followupCalled: false,
            action: `Follow-up needed with ${firstName}`
          });
          setIsCalled(false);
          setCurrentAction(`Follow-up needed with ${firstName}`);
        }
      } else {
        // Handle initial call toggle (existing logic)
        const newCalledStatus = !isCalled;
        const loanSnapshot = await getDoc(loanDocRef);
        
        if (!loanSnapshot.exists()) {
          console.error('Loan document not found');
          return;
        }

        const createdAt = loanSnapshot.data().createdAt?.toDate() || new Date();
        const followUpDate = new Date(createdAt);
        followUpDate.setDate(followUpDate.getDate() + 3);
        
        let newAction = newCalledStatus
          ? (allDocumentsUploaded 
              ? `${firstName} has uploaded all documents. No action required.`
              : `Call ${firstName} to upload documents by ${followUpDate.toLocaleDateString()}`)
          : `${firstName} to see what they are working on (new signup)`;

        await updateDoc(loanDocRef, {
          called: newCalledStatus,
          action: newAction,
          followUpDate: followUpDate,
          displayDate: followUpDate,
          followupCalled: false
        });

        setIsCalled(newCalledStatus);
        setCurrentAction(newAction);
      }
    } catch (error) {
      console.error('Error updating call status:', error);
    }
  };

  const handleNotesChange = async (e) => {
    e.stopPropagation();
    const newNotes = e.target.value;
    setCurrentNotes(newNotes);
    
    try {
      const loanDocRef = doc(db, 'loanApplications', id);
      await updateDoc(loanDocRef, {
        notes: newNotes
      });
    } catch (error) {
      console.error('Error updating notes:', error);
    }
  };

  const handleStarToggle = async (e) => {
    e.stopPropagation();
    try {
      const loanDocRef = doc(db, 'loanApplications', id);
      const newStarredStatus = !isStarred;
      
      await updateDoc(loanDocRef, {
        starred: newStarredStatus
      });
      
      setIsStarred(newStarredStatus);
    } catch (error) {
      console.error('Error updating star status:', error);
    }
  };

  return (
    <div
      className={`group relative w-full overflow-hidden rounded-2xl border bg-white shadow-sm cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${
        isPursuingOtherBrokersDeal ? 'border-purple-300' :
        hasChosenListing ? 'border-blue-300' :
        termSheetRequested ? 'border-amber-300' : 'border-gray-200'
      }`}
      onClick={onClick}
    >
      <div className={`absolute inset-x-0 top-0 h-1 ${isPursuingOtherBrokersDeal ? 'bg-purple-500' : hasChosenListing ? 'bg-blue-500' : termSheetRequested ? 'bg-amber-500' : allDocumentsUploaded ? 'bg-emerald-500' : 'bg-indigo-500'}`} />
      <div className="p-5 pt-6 border-b border-gray-100">
        <div className="flex justify-between items-start mb-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-sm font-bold text-indigo-700">
              {(name || 'L').trim()[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-gray-900">{name}</h3>
              <p className="mt-0.5 text-xs text-gray-500">{loanPurpose || 'Loan application'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleStarToggle}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Star
                className={`w-5 h-5 ${
                  isStarred 
                    ? 'fill-yellow-400 text-yellow-400' 
                    : 'text-gray-400 hover:text-yellow-400'
                }`}
              />
            </button>
            <img src={logo} alt="Company Logo" className="w-5 h-5 opacity-70" />
          </div>
        </div>
        {isPursuingOtherBrokersDeal && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-purple-100 border border-purple-400 rounded-lg">
            <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-purple-800">Pursuing Other Broker's Deal</span>
              {otherBrokerDealName && (
                <span className="text-xs text-purple-600">Deal: {otherBrokerDealName}</span>
              )}
            </div>
          </div>
        )}
        {hasChosenListing && !isPursuingOtherBrokersDeal && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-blue-100 border border-blue-400 rounded-lg">
            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-blue-800">Chosen as Buyer</span>
              {chosenListingName && (
                <span className="text-xs text-blue-600">For: {chosenListingName}</span>
              )}
            </div>
          </div>
        )}
        {termSheetRequested && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-100 border border-amber-300 rounded-lg">
            <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-sm font-semibold text-amber-800">Term Sheet Requested</span>
          </div>
        )}
        {allDocumentsUploaded && !prequalified && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-purple-100 border border-purple-300 rounded-lg">
            <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-semibold text-purple-800">Ready to Prequalify</span>
          </div>
        )}
        <div className="flex items-center text-gray-500 text-sm mb-3">
          <MapPin className="w-4 h-4 mr-2 text-blue-500" />
          <p>{businessState}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm mb-3">
          <span className="text-gray-600">Follow Up Date: </span>
          <span className="font-medium text-blue-600">
            {followUpDate && typeof followUpDate === 'string' ? 
              (() => {
                // Create date in local timezone to avoid timezone conversion issues
                const [year, month, day] = followUpDate.split('-');
                const date = new Date(year, month - 1, day); // month is 0-indexed
                return date.toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                });
              })() : 
              'No follow-up date set'
            }
          </span>
        </div>
        {isCallsTab && (
          <div className="border-2 border-dotted border-gray-300 rounded-md p-2 text-sm mb-3">
            <span className="text-gray-600 block mb-1">Notes: </span>
            <textarea
              className="w-full p-2 text-sm text-gray-700 border border-gray-200 rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Enter call notes here..."
              value={currentNotes}
              onChange={handleNotesChange}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div className="flex items-center justify-between mt-2 p-3 bg-gray-50 rounded-xl">
          <span className="text-sm text-gray-600 flex items-center">
            <Phone className="w-4 h-4 mr-2 text-gray-500" />
            Called Status
          </span>
          <button
            onClick={handleCallToggle}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors duration-200 ${
              isCalled 
                ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                : 'bg-red-100 text-red-800 hover:bg-red-200'
            }`}
          >
            {isCalled ? 'Yes' : 'No'}
          </button>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">
            {isPrequalification ? "Projected Sale Price" : "Loan Amount"}
          </span>
          <div className="flex items-center">
            <DollarSign className="w-5 h-5 text-green-600 mr-1" />
            <span className="text-xl font-bold text-gray-900">
              {Number.isFinite(displayedAmount) && displayedAmount > 0 ? `$${displayedAmount.toLocaleString()}` : 'Not provided'}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-gray-100 rounded-xl border border-gray-100 bg-gray-50 py-3">
          <div className="flex items-center justify-between">
            <div className="px-3">
              <span className="block text-xs font-medium uppercase tracking-wider text-gray-400">Business age</span>
              <div className="mt-1 flex items-center text-sm font-semibold text-gray-800">
                <Calendar className="w-4 h-4 mr-1.5 text-indigo-500" />
                {businessAge || 'N/A'}
              </div>
            </div>
          </div>
          <div className="px-3">
            <span className="block text-xs font-medium uppercase tracking-wider text-gray-400">EBITDA</span>
            <div className="mt-1 flex items-center text-sm font-semibold text-gray-800">
              <TrendingUp className="w-4 h-4 mr-1.5 text-emerald-500" />
              {Number.isFinite(displayedEbitda) && displayedEbitda > 0 ? `$${displayedEbitda.toLocaleString()}` : 'N/A'}
            </div>
          </div>
        </div>
      </div>
      <div className="px-5 py-3 flex items-center justify-between border-t border-gray-100 bg-gray-50">
        <span className="text-gray-600 text-sm">Submitted</span>
        <div className="flex items-center text-gray-700 text-sm">
          <Clock className="w-4 h-4 mr-2 text-blue-500" />
          {submittedAt}
        </div>
      </div>
      <div className="px-5 py-3 flex items-center justify-between border-t border-gray-100 bg-white">
        <span className="text-gray-600 text-sm">Last User Update</span>
        <div className="flex items-center text-gray-700 text-sm">
          <Edit3 className="w-4 h-4 mr-2 text-red-500" />
          {lastUserUpdate ? lastUserUpdate : 'N/A'}
        </div>
      </div>
    </div>
  );
};

export default LightModeLoanCard;
